/* eslint-disable @atlassian/i18n/no-literal-string-in-jsx */

/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import { jsx } from '@compiled/react';

import type { ModalState } from './usePermission';

import ModalDialog, {
	ModalTransition,
	ModalFooter as AKModalFooter,
	ModalBody,
	ModalTitle,
	ModalHeader,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { Box, Text, Stack, Inline } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';

const styles = cssMap({
	titleContainer: {
		marginBottom: token('space.500'),
	},
	modalFooterContainer: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
	},
	disclaimerContainer: {
		marginTop: token('space.400'),
		marginBottom: token('space.400'),
	},
	rowContainer: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
		borderBottomStyle: 'solid',
		borderBottomWidth: token('border.width'),
		borderBottomColor: token('color.border'),
	},
});

export const AdminModal = ({ modalState }: { modalState: ModalState }) => {
	if (modalState.state === 'closed') {
		return <ModalTransition />;
	}

	const ModalFooter = () => {
		return (
			<Box xcss={styles.modalFooterContainer}>
				<AKModalFooter>
					<Inline space="space.075">
						<Button onClick={modalState.onCancel}>Decline</Button>
						<Button onClick={modalState.onConfirm} appearance="primary">
							Approve
						</Button>
					</Inline>
				</AKModalFooter>
			</Box>
		);
	};

	const renderDomains = () => {
		const getHumanReadableName = (operation: string) => {
			switch (operation) {
				case 'FETCH_BACKEND_SIDE':
					return 'Fetch backend side';
				case 'FETCH_CLIENT_SIDE':
					return 'Fetch client side';
				case 'FONTS':
					return 'Fonts';
				case 'FRAMES':
					return 'Frames';
				case 'IMAGES':
					return 'Images';
				case 'MEDIA':
					return 'Media';
				case 'NAVIGATION':
					return 'Navigation';
				case 'SCRIPTS':
					return 'Scripts';
				case 'STYLES':
					return 'Styles';
				default:
					return 'Egress';
			}
		};

		const domainInfo = modalState.domainInfo;

		if (Object.keys(domainInfo).length === 0) {
			return null;
		}

		if (
			domainInfo.type === 'REMOTE' &&
			(domainInfo.payload.configured === null || domainInfo.payload.configured.endpoint === null)
		) {
			return (
				<Stack>
					<Heading size="small">Remote</Heading>
					<Text>This remote is being unset.</Text>
				</Stack>
			);
		}

		if (domainInfo.type === 'REMOTE') {
			return (
				<Stack>
					<Heading size="small">Remote</Heading>
					<Inline xcss={styles.rowContainer} spread="space-between" grow="fill">
						<Text>{domainInfo.payload.configured.endpoint}</Text>
						<Text>{domainInfo.payload.key}</Text>
					</Inline>
				</Stack>
			);
		}

		if (domainInfo.type === 'EGRESS') {
			return domainInfo.payload.egresses.map((e) => (
				<Stack key={e.key} space="space.150">
					<Heading size="xsmall">{e.description}</Heading>
					{e.configured.map((c) => {
						return c.type.map((t) => {
							return (
								<Inline xcss={styles.rowContainer} spread="space-between" grow="fill">
									<Text>{c.domain}</Text>
									<Text>{getHumanReadableName(t)}</Text>
								</Inline>
							);
						});
					})}
				</Stack>
			));
		}
	};

	return (
		<ModalTransition>
			<ModalDialog
				onClose={() => {
					modalState.onCancel();
				}}
				width="large"
			>
				<ModalHeader>
					<Box xcss={styles.titleContainer}>
						<ModalTitle>Data storage, sharing, receiving and processing</ModalTitle>
					</Box>
				</ModalHeader>
				<ModalBody>
					<Stack space="space.300">
						<Text>
							{modalState.appName ?? 'The app'} is requesting to share with and receive data from
							the following domains:
						</Text>
						<Box>
							{renderDomains()}
							<Box xcss={styles.disclaimerContainer}>
								<Text>
									For more details, refer to {modalState.appName ?? 'the app'}'s{' '}
									<Link href={modalState.termsOfUseLink}>terms of use</Link> and{' '}
									<Link href={modalState.privacyPolicyLink}>privacy policy</Link>
								</Text>
							</Box>
						</Box>
					</Stack>
				</ModalBody>
				<ModalFooter />
			</ModalDialog>
		</ModalTransition>
	);
};
